import { ReactNode, useCallback, useContext, useRef, useState, useEffect, createContext } from 'react';
import noop from 'lodash/noop';
import { useTranslation } from 'react-i18next';
import { safeAxios } from '@/services/safeAxios';
import { API_ROUTES } from '@/api/apiRoutes';
import { refreshSubscription } from '@/services/refreshSubscription';
import { Trans } from '@/UI/Trans/Trans';
import ConfirmUpgradeModal from '@/components/SubscriptionsAndPayments/ConfirmUpgradeModal';
import PricingModal from '@/components/SubscriptionsAndPayments/PricingModal';
import { asyncNoop } from '@/utils/functions';
import ConfirmDowngradeModal from '@/components/SubscriptionsAndPayments/ConfirmDowngradeModal';
import GetMoreCreditsModal from '@/components/SubscriptionsAndPayments/GetMoreCreditsModal';
import CreditsOrPricingModal from '@/components/SubscriptionsAndPayments/CreditsOrPricingModal';
import { SubscriptionType } from '@/types/shared';
import { PricingPackage, GetMoreCreditsRequest } from '@/components/SubscriptionsAndPayments/payments.types';
import { I18N_KEYS } from '@/language/keys';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useGlobalStore } from '@/state/global.state';
import useWorkspacesStore from '@/state/workspaces.state';
import { availableCreditsForUser, showsCreditsToMembers, workspaceCreditsTotal } from '@/utils/workspace-credits.utils';
import { AuthContext } from '../contexts/AuthContext';
import type { PricingPageViewAnalyticsContext } from '@/enums/pricing-page.enum';
import type { ModelRunCredits } from '@/api/modelPrediction';

interface CreditsContextStore {
  credits: number | null;
  workspaceCredits: number | null;
  handleConfirmDowngrade: (toPackage: PricingPackage) => Promise<void>;
  handleConfirmUpgrade: (toPackage: PricingPackage, numberOfSeats?: number) => Promise<void>;
  handleGetMoreCreditsClick: () => void;
  hasError: boolean;
  openDowngradeSubscriptionModal: (toPackage: PricingPackage) => void;
  openUpgradeModal: (options?: { onOpen?: () => void; analyticsContext?: PricingPageViewAnalyticsContext }) => void;
  openUpgradeModalFromEntrypoint: (analyticsContext: PricingPageViewAnalyticsContext) => void;
  openUpgradeSubscriptionModal: (toPackage: PricingPackage) => void;
  setShouldShowCreditsToMembers: (shouldShowCreditsToMembers: boolean) => void;
  setUserCredits: (credits: number | null) => void;
  setWorkspaceCredits: (credits: number | null) => void;
  shouldShowCreditsToMembers: boolean;
  subscriptionType: SubscriptionType | null;
  isPricingPlansModalOpen: boolean;
}

export const CreditsContext = createContext<CreditsContextStore>({
  credits: null,
  workspaceCredits: null,
  handleConfirmDowngrade: asyncNoop,
  handleConfirmUpgrade: asyncNoop,
  handleGetMoreCreditsClick: noop,
  hasError: false,
  openDowngradeSubscriptionModal: noop,
  openUpgradeModal: noop,
  openUpgradeModalFromEntrypoint: noop,
  openUpgradeSubscriptionModal: noop,
  setShouldShowCreditsToMembers: noop,
  setUserCredits: noop,
  setWorkspaceCredits: noop,
  shouldShowCreditsToMembers: true,
  subscriptionType: null,
  isPricingPlansModalOpen: false,
});

/** Sync detached model charges/refunds into the displayed balances. */
export function useModelRunCredits() {
  const { setUserCredits, setWorkspaceCredits } = useContext(CreditsContext);
  return useCallback(
    (credits: ModelRunCredits) => {
      // The server sends what is left of the member's monthly cap, which can be more than the workspace has.
      // Like availableCreditsForUser, the member can spend only the smaller of the two.
      const { userRemainingCredits, remainingCredits } = credits;
      if (typeof userRemainingCredits === 'number') {
        const spendable = typeof remainingCredits === 'number' ? Math.min(userRemainingCredits, remainingCredits) : userRemainingCredits;
        setUserCredits(Math.max(0, spendable));
      } else if (userRemainingCredits === null) {
        setUserCredits(null);
      }
      if (credits.remainingCredits !== undefined) setWorkspaceCredits(credits.remainingCredits);
    },
    [setUserCredits, setWorkspaceCredits],
  );
}

type CreditsProviderProps = {
  children: ReactNode;
};

export const CreditsProvider = ({ children }: CreditsProviderProps) => {
  const { t: translate } = useTranslation();
  const { currentUser, enrichUser } = useContext(AuthContext);
  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);
  const [credits, setCredits] = useState<number | null>(null);
  const [workspaceCredits, setWorkspaceCredits] = useState<number | null>(null);
  const [currentSubscriptionType, setCurrentSubscriptionType] = useState<SubscriptionType | null>(null);
  const { track } = useAnalytics();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showGetMoreCreditsModal, setShowGetMoreCreditsModal] = useState(false);
  const [showPricingPlansModal, setShowPricingPlansModal] = useState(false);
  const [showCreditOrPricingModal, setShowCreditOrPricingModal] = useState(false);
  const [pricingPageAnalyticsContext, setPricingPageAnalyticsContext] = useState<PricingPageViewAnalyticsContext>();
  const pricingModalOnOpenRef = useRef<(() => void) | null>(null);

  const [shouldShowCreditsToMembers, setShouldShowCreditsToMembers] = useState(true);
  const [showUpgradeSubscriptionModal, setShowUpgradeSubscriptionModal] = useState(false);
  const [upgradeToPackage, setUpgradeToPackage] = useState<PricingPackage>();
  const [downgradeToPackage, setDowngradeToPackage] = useState<PricingPackage>();
  const [showDowngradeSubscriptionModal, setShowDowngradeSubscriptionModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setCredits(availableCreditsForUser(currentUser.activeWorkspace));
      setWorkspaceCredits(workspaceCreditsTotal(currentUser.activeWorkspace));
      setShouldShowCreditsToMembers(showsCreditsToMembers(currentUser.activeWorkspace));
      setCurrentSubscriptionType(currentUser.activeWorkspace.subscription?.type);
    }
  }, [currentUser]);

  const openPricingModal = (analyticsContext?: PricingPageViewAnalyticsContext, onOpen?: () => void) => {
    localStorage.setItem('lastPageBeforePayment', window.location.href);
    pricingModalOnOpenRef.current = onOpen ?? null;
    setPricingPageAnalyticsContext(analyticsContext);
    setShowUpgradeModal(true);
  };

  const openUpgradeModal = (options?: { onOpen?: () => void; analyticsContext?: PricingPageViewAnalyticsContext }) =>
    openPricingModal(options?.analyticsContext, options?.onOpen);

  const openUpgradeModalFromEntrypoint = (analyticsContext: PricingPageViewAnalyticsContext) =>
    openPricingModal(analyticsContext);

  const handlePricingModalOpen = useCallback(() => {
    const onOpen = pricingModalOnOpenRef.current;
    pricingModalOnOpenRef.current = null;
    onOpen?.();
  }, []);

  const handleGetMoreCreditsClick = () => {
    track(
      'Clicked_get_more_credits',
      {
        context: 'Payments',
      },
      TrackTypeEnum.BI,
    );
    localStorage.setItem('lastPageBeforePayment', window.location.href);
    if (currentSubscriptionType === SubscriptionType.Free) {
      setShowPricingPlansModal(true);
    } else if (
      currentSubscriptionType !== SubscriptionType.Team &&
      currentSubscriptionType !== SubscriptionType.Enterprise
    ) {
      setShowCreditOrPricingModal(true);
    } else {
      setShowGetMoreCreditsModal(true);
    }
  };

  const openUpgradeSubscriptionModal = (toPackage: PricingPackage) => {
    track(
      'Opened_upgrade_subscription_modal',
      {
        package: toPackage.name,
        context: 'Payments',
      },
      TrackTypeEnum.BI,
    );
    setHasError(false);
    setShowUpgradeSubscriptionModal(true);
    setUpgradeToPackage(toPackage);
  };

  const openDowngradeSubscriptionModal = (toPackage: PricingPackage) => {
    track(
      'Pricing - Opened_downgrade_subscription_modal',
      {
        package: toPackage.name,
        context: 'Payments',
      },
      TrackTypeEnum.BI,
    );
    setHasError(false);
    setShowDowngradeSubscriptionModal(true);
    setDowngradeToPackage(toPackage);
  };

  const upgradeSuccessMessage = (toPackage: PricingPackage) => (
    <Trans
      i18nKey={translate(I18N_KEYS.PAYMENTS.UPGRADE_SUCCESS_MESSAGE)}
      components={{
        plan: <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{toPackage.name}</span>,
      }}
    />
  );

  const handleConfirmUpgrade = async (toPackage: PricingPackage, numberOfSeats: number = 1) => {
    const workspaceId = currentUser?.activeWorkspace?.workspaceId;
    if (!workspaceId) return;
    track(
      'Pricing - Confirmed_upgrade_subscription',
      {
        package: toPackage.name,
        numberOfSeats,
        context: 'Payments',
        entrypoint: toPackage.entrypoint,
      },
      TrackTypeEnum.BI,
    );
    // TODO: handle upgrade / downgrade
    try {
      setHasError(false);
      setIsProcessingPayment(true);
      const response = await safeAxios.post(API_ROUTES.workspaces.subscriptionUpgrade({ workspaceId }), {
        subscriptionType: toPackage.name,
        billingCycle: toPackage.billingCycle,
        numberOfSeats: numberOfSeats,
      });
      if (response.status === 200) {
        await refreshSubscription(enrichUser, workspaceId, toPackage.name, toPackage.billingCycle);
        setShowUpgradeModal(false);
        setShowPricingPlansModal(false);
        setPricingPageAnalyticsContext(undefined);
        updateSnackbarData({
          text: upgradeSuccessMessage(toPackage),
          isOpen: true,
          severity: 'success',
          icon: null,
          action: null,
          duration: 5000,
          showCloseButton: true,
        });
        setShowUpgradeSubscriptionModal(false);
      }
    } catch (error) {
      setHasError(true);
      throw error;
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const downgradeSuccessMessage = (toPackage: PricingPackage) => (
    <Trans
      i18nKey={translate(I18N_KEYS.PAYMENTS.DOWNGRADE_SUCCESS_MESSAGE)}
      components={{
        plan: <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{toPackage.name}</span>,
      }}
    />
  );
  const handleConfirmDowngrade = async (toPackage: PricingPackage) => {
    track(
      'Pricing - Confirmed_downgrade_subscription',
      {
        package: toPackage.name,
        context: 'Payments',
      },
      TrackTypeEnum.BI,
    );
    // TODO: handle downgrade
    try {
      setHasError(false);
      setIsProcessingPayment(true);
      const response = await safeAxios.post(
        API_ROUTES.workspaces.subscriptionDowngrade({ workspaceId: currentUser?.activeWorkspace?.workspaceId }),
        {
          subscriptionType: toPackage.name,
          billingCycle: toPackage.billingCycle,
        },
      );
      if (response.status === 200) {
        await enrichUser();
        setShowUpgradeModal(false);
        setShowPricingPlansModal(false);
        setPricingPageAnalyticsContext(undefined);
        updateSnackbarData({
          text: downgradeSuccessMessage(toPackage),
          isOpen: true,
          severity: 'success',
          icon: null,
          action: null,
          duration: 5000,
          showCloseButton: true,
        });
      }
    } catch (error) {
      setHasError(true);
      throw error;
    } finally {
      setIsProcessingPayment(false);
      setShowDowngradeSubscriptionModal(false);
    }
  };

  const handleConfirmGetMoreCredits = async (creditsRequest: GetMoreCreditsRequest) => {
    track(
      'Pricing - Confirmed_get_more_credits',
      {
        credits: creditsRequest.credits,
        payAmountInUsd: creditsRequest.payAmountInUsd,
        context: 'Payments',
      },
      TrackTypeEnum.BI,
    );
    // round price
    creditsRequest.payAmountInUsd = Math.round(creditsRequest.payAmountInUsd * 100) / 100;
    try {
      setIsProcessingPayment(true);
      setHasError(false);
      const response = await safeAxios.post(
        API_ROUTES.workspaces.subscriptionCreditsCheckoutSession({
          workspaceId: currentUser?.activeWorkspace?.workspaceId,
        }),
        creditsRequest,
      );
      const checkoutSessionUrl = response.data.checkoutSessionUrl;
      if (checkoutSessionUrl) {
        try {
          // Snapshot the pre-checkout plan; the store often shows the new plan by the time we're back.
          localStorage.setItem(
            'previousPlanBeforePayment',
            useWorkspacesStore.getState().activeWorkspace.subscription?.type ?? 'unknown',
          );
        } catch {
          // Snapshot is best-effort; never block the checkout redirect on it.
        }
        window.location.href = checkoutSessionUrl;
      }
    } catch (error) {
      setHasError(true);
      throw error;
    } finally {
      setIsProcessingPayment(false);
    }
  };

  useEffect(() => {
    // close modals on browser back/forward button
    const handlePopState = () => {
      pricingModalOnOpenRef.current = null;
      setShowPricingPlansModal(false);
      setShowUpgradeModal(false);
      setShowCreditOrPricingModal(false);
      setShowGetMoreCreditsModal(false);
      setPricingPageAnalyticsContext(undefined);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <CreditsContext.Provider
      value={{
        credits,
        workspaceCredits,
        handleConfirmDowngrade,
        handleConfirmUpgrade,
        handleGetMoreCreditsClick,
        hasError,
        openDowngradeSubscriptionModal,
        openUpgradeModal,
        openUpgradeModalFromEntrypoint,
        openUpgradeSubscriptionModal,
        setShouldShowCreditsToMembers,
        setUserCredits: setCredits,
        setWorkspaceCredits: setWorkspaceCredits,
        shouldShowCreditsToMembers,
        subscriptionType: currentSubscriptionType,
        isPricingPlansModalOpen: showUpgradeModal || showPricingPlansModal,
      }}
    >
      {children}
      {currentUser && (
        <>
          {showGetMoreCreditsModal && (
            <GetMoreCreditsModal
              open={showGetMoreCreditsModal}
              onClose={() => setShowGetMoreCreditsModal(false)}
              currentUser={currentUser}
              isProcessing={isProcessingPayment}
              hasError={hasError}
              setHasError={setHasError}
              onConfirm={handleConfirmGetMoreCredits}
            />
          )}
          {(showPricingPlansModal || showUpgradeModal) && (
            <PricingModal
              open={showUpgradeModal || showPricingPlansModal}
              analyticsContext={pricingPageAnalyticsContext}
              onOpen={handlePricingModalOpen}
              closeModal={() => {
                pricingModalOnOpenRef.current = null;
                setShowPricingPlansModal(false);
                setShowUpgradeModal(false);
                setPricingPageAnalyticsContext(undefined);
              }}
            />
          )}
          {showCreditOrPricingModal && (
            <CreditsOrPricingModal
              open={showCreditOrPricingModal}
              onClose={() => setShowCreditOrPricingModal(false)}
              setShowGetMoreCreditsModal={setShowGetMoreCreditsModal}
              setShowPricingPlansModal={setShowPricingPlansModal}
            />
          )}
        </>
      )}
      {currentUser && upgradeToPackage && showUpgradeSubscriptionModal && (
        <ConfirmUpgradeModal
          open={showUpgradeSubscriptionModal}
          onClose={() => setShowUpgradeSubscriptionModal(false)}
          toPackage={upgradeToPackage}
          onConfirm={handleConfirmUpgrade}
          isProcessing={isProcessingPayment}
          currentUser={currentUser}
          hasError={hasError}
        />
      )}
      {currentUser && downgradeToPackage && showDowngradeSubscriptionModal && (
        <ConfirmDowngradeModal
          currentUser={currentUser}
          open={showDowngradeSubscriptionModal}
          onClose={() => setShowDowngradeSubscriptionModal(false)}
          toPackage={downgradeToPackage}
          onConfirm={handleConfirmDowngrade}
          isProcessing={isProcessingPayment}
          hasError={hasError}
        />
      )}
    </CreditsContext.Provider>
  );
};

export const useCreditsContext = () => {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCreditsContext must be used within a CreditsProvider');
  }
  return context;
};
